import React, { useState } from 'react';
import styled from 'styled-components';
import { formatters, cartoesAPI } from '../services/api';
import { toast } from 'react-toastify';
import { LuIdCard, LuLoaderCircle, LuUser, LuUsers } from 'react-icons/lu';
import { downloadBlob } from '../utils/downloadUtils';

const TitularContainer = styled.div`
  margin-bottom: 30px;
`;

const TitularGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 20px;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const TitularCardContainer = styled.div`
  background: ${props => props.theme.colors.white};
  border: 1px solid #e2e8f0;
  border-left: 5px solid #1e3a8a2e;
  border-radius: 16px;
  padding: 24px;
  position: relative;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
    border-color: #1e3a8a;
    border-left-color: #1e3a8a;
  }

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const StatusWrapper = styled.div`
  position: absolute;
  top: 24px;
  right: 24px;
`;

const StatusBadge = styled.span`
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: #dcfce7;
  color: #166534;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-right: 70px;
`;

const Avatar = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: #dbeafe;
  color: #1e3a8a;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  flex-shrink: 0;
`;

const HeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const TitularName = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  line-height: 1.2;
`;

const MemberType = styled.div`
  font-size: 13px;
  color: ${props => props.theme.colors.grayMedium};
  font-weight: 600;
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  padding: 16px 0;
  border-top: 1px solid #f1f5f9;
  border-bottom: 1px solid #f1f5f9;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const InfoLabel = styled.div`
  color: ${props => props.theme.colors.grayMedium};
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
`;

const InfoValue = styled.div`
  color: ${props => props.theme.colors.textPrimary};
  font-size: 15px;
  font-weight: 600;
  margin-top: 2px;
`;

const ActionArea = styled.div`
  display: flex;
  gap: 10px;
  margin-top: auto;
`;

const ActionButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: ${props => props.theme.colors.secondary};
  
  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.secondary};
    color: white;
    border-color: ${props => props.theme.colors.secondary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .spinner {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const TitularCard = ({ cliente, contrato }) => {
  const [isGenerating, setIsGenerating] = useState(false);

  if (!cliente) return null;

  const handleGenerateCard = async () => {
    const numeroContrato = contrato?.numero;
    if (!numeroContrato) {
      toast.error("Número do contrato não encontrado. Não é possível gerar o cartão.");
      return;
    }
    
    setIsGenerating(true);
    try {
      toast.info('Gerando cartão, por favor aguarde...');
      const response = await cartoesAPI.gerar([{ nome: cliente.nome }], numeroContrato);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const filename = `cartao-${cliente.nome.replace(/\s+/g, '_')}-${numeroContrato}.pdf`;
      await downloadBlob(blob, filename);
      toast.success('Cartão gerado com sucesso!');
    } catch (error) {
      console.error("Falha na geração do cartão do titular:", error);
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao gerar cartão';
      toast.error(`Erro ao gerar cartão: ${errorMessage}`);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <TitularContainer>
      <TitularGrid>
        <TitularCardContainer>
          {/*
            -- Desativado temporariamente para não confundir o usuário --
            <StatusWrapper>
              <StatusBadge>Ativo</StatusBadge>
            </StatusWrapper>
          */}

          <CardHeader>
            <Avatar>
              <LuUser />
            </Avatar>
            <HeaderInfo>
              <TitularName>{cliente.nome || 'N/A'}</TitularName>
              <MemberType>
                <LuUsers size={14} /> Titular do Plano
              </MemberType>
            </HeaderInfo>
          </CardHeader>
          
          <InfoGrid>
            <InfoItem>
              <InfoLabel>Idade</InfoLabel>
              <InfoValue>{cliente.idade ?? 'N/A'}</InfoValue>
            </InfoItem>
            
            <InfoItem>
              <InfoLabel>Tipo</InfoLabel>
              <InfoValue>Titular</InfoValue>
            </InfoItem>

            <InfoItem>
              <InfoLabel>Contrato</InfoLabel>
              <InfoValue>#{contrato?.numero || 'N/A'}</InfoValue>
            </InfoItem>

            <InfoItem>
              <InfoLabel>Sexo</InfoLabel>
              <InfoValue>{cliente.sexo === 'M' ? 'Masc.' : cliente.sexo === 'F' ? 'Fem.' : 'N/A'}</InfoValue>
            </InfoItem>
          </InfoGrid>

          <ActionArea>
            <ActionButton
              onClick={handleGenerateCard}
              disabled={isGenerating}
            >
              {isGenerating ? (
                <LuLoaderCircle className="spinner" />
              ) : (
                <LuIdCard />
              )}
              {isGenerating ? 'Gerando...' : 'Gerar Cartão'}
            </ActionButton>
          </ActionArea>
        </TitularCardContainer>
      </TitularGrid>
    </TitularContainer>
  );
};

export default TitularCard;
